// Modules
import { useEffect } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FormattedMessage } from "react-intl";
import get from "lodash.get";
import cloneDeep from "lodash.clonedeep";
// Material-UI
import { useTheme } from "@mui/material/styles";
import {
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
// Components
import InitializeLocation from "../../../AppLayout/InitializeLocation";
// Helpers
import { FREQ_NONE, RANGE_ALL, RANGE_CURRENT, RANGE_CURRENT_FOLLOWING } from "../../../../helpers/constants";
import { RECURRENCE } from "./helpers/recurrence";
import { addExceptionDate } from "./helpers/calendar";
import { getEmail } from "../../../../helpers/userData";
// Styles
import { gridSpacing } from "../../../../styles/constants";
// Assets
import DateRangeIcon from "@mui/icons-material/DateRange";
import DeleteIcon from "@mui/icons-material/Delete";

const getMinUntilDate = date => {
    let minUntilDate = new Date(date);
    minUntilDate.setDate(minUntilDate.getDate() + 1);

    return minUntilDate;
};

const getInitialValues = ({
    editMode,
    event,
    creationType,
    recurrenceRange,
    theme,
    updateEvent,
    selectedTherapist
}) => {
    const getNewDate = () => {
        let date = new Date();
        date.setHours(date.getHours() + 1, 0, 0, 0);

        return date;
    };

    const defaultRrule = {
        freq: FREQ_NONE
    };

    let rrule = updateEvent?.rrule || defaultRrule;
    let recurrence = updateEvent?.recurrence || defaultRrule.freq;
    let exdate = updateEvent?.exdate;
    let exrule = updateEvent?.exrule;
    if (recurrenceRange === RANGE_CURRENT) {
        rrule = defaultRrule;
        recurrence = defaultRrule.freq;
        exdate = undefined;
        exrule = undefined;
    }
    if (rrule.until) {
        rrule.until = new Date(rrule.until);
    }

    let startDate = updateEvent?.start ? new Date(updateEvent.start) : getNewDate();
    if (event && recurrenceRange === RANGE_ALL) {
        // If changing a recurrent event and selected to change all
        // then the start date will be the first/initial event date
        startDate = new Date(event?.start);
        // Set the hours based on the change request (drag & drop case)
        startDate.setHours(updateEvent.start.getHours(), 0, 0, 0);
        const eventStartDate = new Date(event?.start);
        // Take into account when there is timezone difference
        const startDateTimezoneOffset = eventStartDate.getTimezoneOffset();
        const updateStartDateTimezoneOffset = updateEvent?.start.getTimezoneOffset();
        if (startDateTimezoneOffset !== updateStartDateTimezoneOffset) {
            const timezoneDiff = updateStartDateTimezoneOffset - startDateTimezoneOffset;
            const diffHours = Math.trunc(timezoneDiff / 60);
            const diffMinutes = (timezoneDiff / 60 - diffHours) * 60;

            startDate.setHours(startDate.getHours() + diffHours, startDate.getMinutes() + diffMinutes, 0, 0);
        }
    }

    const eventType = editMode ? updateEvent?.type || creationType : creationType;

    const initialValues = {
        id: updateEvent?.id,
        type: eventType,
        title: updateEvent?.title || "",
        description: updateEvent?.description || "",
        color: updateEvent?.color || theme.palette.primary["200"],
        start: startDate,
        allDay: updateEvent?.allDay || false,
        rrule,
        recurrence,
        exdate,
        exrule,
        duration: "01:00", // fixed 1h duration
        therapistId: selectedTherapist?._id ?? null
    };

    return initialValues;
};

const validationSchema = Yup.object().shape({
    start: Yup.date().required(<FormattedMessage id="error-required-field" />),
    color: Yup.string().max(255)
});

const EditFreeSlotForm = ({
    isAdmin,
    event,
    creationType,
    handleDelete,
    handleCreate,
    handleUpdate,
    locale,
    onCancel,
    recurrenceRange,
    updateEvent,
    therapistsList,
    selectedTherapists,
    handleTherapistSelection
}) => {
    const theme = useTheme();
    const editMode = !!event?.id;

    const selectedTherapist = selectedTherapists?.length === 1 ? selectedTherapists[0] : "";
    const userEmail = getEmail();

    const formik = useFormik({
        initialValues: getInitialValues({
            editMode,
            event,
            creationType,
            recurrenceRange,
            theme,
            updateEvent,
            selectedTherapist
        }),
        validationSchema,
        onSubmit: async (values, { setErrors }) => {
            const selectedTherapistId = values.therapistId;
            // If admin and therapistId empty, throw required error
            if (isAdmin && selectedTherapistId === null) {
                setErrors({ therapistId: <FormattedMessage id="error-required-field" /> });
                return;
            }
            let startDate = values.start;

            if (values.recurrence !== FREQ_NONE && values.rrule?.until && startDate >= values.rrule.until) {
                setErrors({ rrule: { until: <FormattedMessage id="error-invalid-date-min-start-date" /> } });
                return;
            }

            let endDate = new Date(values.start);
            endDate.setHours(endDate.getHours() + 1);

            let eventData = {
                id: values.id,
                type: values.type,
                title: values.title && values.title.length > 0 ? values.title : undefined,
                description: values.description && values.description.length > 0 ? values.description : undefined,
                color: values.color,
                start: startDate?.toISOString(),
                end: endDate?.toISOString(),
                guests: values.guests,
                allDay: values.allDay,
                rrule:
                    values.recurrence === FREQ_NONE
                        ? undefined
                        : {
                              ...values.rrule,
                              ...(RECURRENCE[values.recurrence]?.props || {}),
                              interval:
                                  (RECURRENCE[values.recurrence]?.hasCustomInterval ? values.rrule?.interval : 1) || 1,
                              dtstart: startDate?.toISOString(),
                              until: values.rrule?.until?.toISOString()
                          },
                recurrence: values.recurrence,
                exdate: values.exdate,
                exrule: values.exrule,
                duration: values.duration,
                createdBy: userEmail
            };

            if (editMode) {
                if (recurrenceRange === RANGE_ALL) {
                    // If time is changed, update the exdates to match the time
                    const initialStartDateTime = new Date(event.start)?.toISOString();
                    const initialStartTime = initialStartDateTime.split("T")[1];
                    const dataStartTime = eventData.start.split("T")[1];

                    if (initialStartTime !== dataStartTime && eventData.exdate) {
                        eventData.exdate = eventData.exdate?.map(e => `${e.split("T")[0]}T${dataStartTime}`);
                    }
                } else if (recurrenceRange === RANGE_CURRENT) {
                    // Create a new event for the single change
                    let newEventData = cloneDeep(eventData);
                    Reflect.deleteProperty(newEventData, "id");
                    handleCreate({ data: newEventData, id: selectedTherapistId });

                    // Add an exception (of this date) to the original recurring event
                    eventData = cloneDeep(event);
                    eventData.start = new Date(eventData.start)?.toISOString();
                    eventData.end = new Date(eventData.end)?.toISOString();

                    const exception = updateEvent.dragDropEventStart || formik.initialValues.start;
                    addExceptionDate(eventData, exception);
                } else if (recurrenceRange === RANGE_CURRENT_FOLLOWING) {
                    // Create a new recurring event
                    let newEventData = cloneDeep(eventData);
                    Reflect.deleteProperty(newEventData, "id");
                    handleCreate({ data: newEventData, id: selectedTherapistId });

                    // Update existing recurring event with an end date
                    eventData = cloneDeep(event);
                    eventData.start = new Date(eventData.start)?.toISOString();
                    eventData.end = new Date(eventData.end)?.toISOString();

                    eventData.rrule.until = formik.initialValues.start.toISOString().split("T")[0];
                }

                handleUpdate({ eventId: event.id, update: eventData, userId: selectedTherapistId });
            } else {
                handleCreate({ data: eventData, id: selectedTherapistId });
            }
        }
    });

    useEffect(() => {
        return () => {
            formik?.resetForm();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { values, errors, getFieldProps, setFieldValue } = formik;

    const handleSubmit = e => {
        e.preventDefault();

        formik.handleSubmit(e);
    };

    return (
        <InitializeLocation adapter="date" locale={locale}>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{<FormattedMessage id={event ? "update-event" : "create-event"} />}</DialogTitle>
                <Divider />
                {isAdmin && therapistsList && (
                    <DialogContent>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={6} sx={{ minWidth: "220px" }}>
                                <Autocomplete
                                    id="therapist"
                                    disablePortal
                                    disabled={editMode}
                                    getOptionLabel={option => option.name || ""}
                                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                    noOptionsText={<FormattedMessage id="no-options" />}
                                    options={therapistsList || []}
                                    onChange={(_, value) => {
                                        if (value) {
                                            const { _id } = value;
                                            setFieldValue("therapistId", value?._id);
                                            handleTherapistSelection(_id, true, true);
                                        } else {
                                            setFieldValue("therapistId", null);
                                            handleTherapistSelection(null, true, true);
                                        }
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            id="therapistId"
                                            name="therapistId"
                                            label={
                                                <span>
                                                    <FormattedMessage id="select-therapist" />
                                                </span>
                                            }
                                            error={
                                                get(formik.touched, "therapistId", undefined) &&
                                                Boolean(get(formik.errors, "therapistId", undefined))
                                            }
                                            helperText={
                                                get(formik.touched, "therapistId", undefined) &&
                                                get(formik.errors, "therapistId", undefined)
                                            }
                                        />
                                    )}
                                    value={
                                        therapistsList?.find(
                                            el => el?._id === get(formik.values, "therapistId", null)
                                        ) ?? null
                                    }
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={6} sx={{ minWidth: "220px" }}>
                            <MobileDateTimePicker
                                slotProps={{
                                    textField: {
                                        inputProps: { "data-testid": "therapy-start" }
                                    }
                                }}
                                ampm={false}
                                views={["day", "month", "year", "hours"]}
                                label={
                                    <span>
                                        <FormattedMessage id={"start-date"} />
                                        {"*"}
                                    </span>
                                }
                                format="dd/MM/yyyy HH:mm"
                                onChange={date => setFieldValue("start", date)}
                                okText={<FormattedMessage id="ok" />}
                                cancelText={<FormattedMessage id="cancel" />}
                                textField={params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <DateRangeIcon />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                                value={values.start}
                            />
                        </Grid>
                        <Grid item alignSelf="center">
                            <Typography variant="body1">
                                <FormattedMessage id="1hour-event" />
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1">
                                        <FormattedMessage id="recurrence" />*
                                    </Typography>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={6} sx={{ minWidth: "min-content" }}>
                                        <FormControl>
                                            <RadioGroup
                                                row
                                                aria-label="recurrence"
                                                {...getFieldProps("recurrence")}
                                                onChange={e => setFieldValue("recurrence", e.target.value)}
                                                name="recurrence-radio-buttons-group"
                                                sx={{ "& .MuiFormControlLabel-root": { mr: 0 } }}
                                            >
                                                {Object.entries(RECURRENCE).map(([item, value]) => (
                                                    <Grid item xs={12} key={item}>
                                                        <FormControlLabel
                                                            value={item}
                                                            control={<Radio />}
                                                            label={
                                                                value?.getLabel ? (
                                                                    value.getLabel(formik)
                                                                ) : (
                                                                    <FormattedMessage id={item} />
                                                                )
                                                            }
                                                            sx={{ minWidth: "max-content" }}
                                                        />
                                                    </Grid>
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} alignSelf="flex-end" sx={{ mt: "32px", minWidth: "200px" }}>
                                        <MobileDatePicker
                                            clearable
                                            slotProps={{
                                                textField: {
                                                    inputProps: { "data-testid": "repeats-until" }
                                                }
                                            }}
                                            disabled={values.recurrence === FREQ_NONE}
                                            label={<FormattedMessage id={"repeats-until"} />}
                                            value={values.rrule?.until || null}
                                            format="dd/MM/yyyy"
                                            minDate={getMinUntilDate(values.start)}
                                            defaultCalendarMonth={getMinUntilDate(values.start)}
                                            onChange={date => {
                                                // To include that day
                                                date && date.setHours(23, 59, 59, 999);
                                                setFieldValue("rrule.until", date);
                                            }}
                                            okText={<FormattedMessage id="ok" />}
                                            cancelText={<FormattedMessage id="cancel" />}
                                            clearText={<FormattedMessage id="clear" />}
                                            textField={params => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <DateRangeIcon />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    error={Boolean(errors.rrule?.until)}
                                                    helperText={errors.rrule?.until}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            {editMode && (
                                <Tooltip title={<FormattedMessage id="delete" />}>
                                    <IconButton onClick={() => handleDelete(event)} size="large">
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Grid>
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button type="button" variant="outlined" onClick={onCancel}>
                                    <FormattedMessage id="cancel" />
                                </Button>
                                <Button type="submit" variant="contained" data-testid="create-update-event">
                                    <FormattedMessage id={editMode ? "edit" : "create"} />
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>
            </form>
        </InitializeLocation>
    );
};

EditFreeSlotForm.propTypes = {
    isAdmin: PropTypes.bool,
    event: PropTypes.object,
    creationType: PropTypes.string,
    handleCreate: PropTypes.func,
    handleDelete: PropTypes.func,
    handleUpdate: PropTypes.func,
    locale: PropTypes.object,
    onCancel: PropTypes.func,
    recurrenceRange: PropTypes.string,
    updateEvent: PropTypes.object,
    therapistsList: PropTypes.array,
    selectedTherapists: PropTypes.array,
    handleTherapistSelection: PropTypes.func
};

export default EditFreeSlotForm;
